import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { SKIP, visit } from 'unist-util-visit'
import { validSources } from './ai-chat-sources'
import type { Components, Options } from 'react-markdown'
import type { Link, Nodes, Parent, Root, Text } from 'mdast'
import type { AiChatSource } from './ai-chat-sources'
import './ai-chat-markdown.css'

const citationPattern = /(\[\d+\])/
const allowedProtocols = ['http:', 'https:', 'mailto:']
const skippedParents = new Set(['link', 'linkReference', 'image', 'code'])

function isSafeUrl(value: string): boolean {
  const url = value.trim()
  if (!url || url.startsWith('//')) return false
  const colon = url.indexOf(':')
  const delimiter = url.search(/[/?#]/)
  if (colon === -1 || (delimiter !== -1 && delimiter < colon)) return true
  try {
    const parsed = new URL(url)

    return (
      allowedProtocols.includes(parsed.protocol) &&
      !parsed.username &&
      !parsed.password
    )
  } catch {
    return false
  }
}

function isExternal(href: string): boolean {
  return /^(https?:)/i.test(href.trim())
}

function transformUrl(url: string): string {
  return isSafeUrl(url) ? url : ''
}

function citationNodes(value: string, sources: Array<AiChatSource>) {
  return value.split(citationPattern).flatMap((part): Array<Text | Link> => {
    if (!part) return []
    const source = sources.find((candidate) => `[${candidate.id}]` === part)

    return source
      ? [
          {
            type: 'link',
            url: source.url,
            title: source.title,
            children: [{ type: 'text', value: part }],
          },
        ]
      : [{ type: 'text', value: part }]
  })
}

function remarkCitations(sources: Array<AiChatSource>) {
  return () => (tree: Root) => {
    if (!sources.length) return
    visit(tree, 'text', (node: Text, index, parent: Parent | undefined) => {
      if (!parent || index === undefined) return
      if (skippedParents.has((parent as Nodes).type)) return SKIP
      if (!citationPattern.test(node.value)) return
      const replacement = citationNodes(node.value, sources)
      parent.children.splice(index, 1, ...replacement)

      return index + replacement.length
    })
  }
}

const components: Components = {
  a({ href, children, node: _node, ...props }) {
    if (!href) return <span>{children}</span>
    const external = isExternal(href)

    return (
      <a
        {...props}
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    )
  },
  img({ alt }) {
    return alt ? <span>{alt}</span> : null
  },
  input({ node: _node, ...props }) {
    return props.type === 'checkbox' ? (
      <input {...props} disabled readOnly />
    ) : null
  },
  table({ children }) {
    return (
      <div className="ai-chat-markdown-scroll" tabIndex={0}>
        <table>{children}</table>
      </div>
    )
  },
  pre({ node: _node, ...props }) {
    return <pre {...props} tabIndex={0} />
  },
}

const rehypePlugins: Options['rehypePlugins'] = [rehypeHighlight]

export function AiChatMarkdown({
  content,
  sources,
}: {
  content: string
  sources: Array<AiChatSource>
}) {
  const remarkPlugins = [remarkGfm, remarkCitations(validSources(sources))]

  return (
    <div className="ai-chat-markdown">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
        urlTransform={transformUrl}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
